import { Box, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ChartCard from "./ChartCard";

const MonthlyLineChart = ({ data, isRTL }) => {
  const { t } = useTranslation();

  const { xLabels, counts, hasData } = useMemo(() => {
    const items = data || [];
    const labels = items.map((m) => (isRTL ? m.label_ar : m.label_en));
    const values = items.map((m) => m.count);
    return {
      xLabels: labels,
      counts: values,
      hasData: values.some((v) => v > 0),
    };
  }, [data, isRTL]);

  const useRotatedLabels = xLabels.length > 6;

  return (
    <ChartCard
      title={t("reports.charts.monthlyLine")}
      subtitle={t("reports.charts.monthlyLineSubtitle")}
      isRTL={isRTL}
      height={400}
      exportCategories={xLabels}
      exportSeries={[{ name: t("reports.charts.ticketCount"), data: counts }]}
    >
      {(layoutHeight, _expanded, showLabels) =>
        !hasData ? (
          <Box
            sx={{
              height: layoutHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">{t("reports.noData")}</Typography>
          </Box>
        ) : (
          <LineChart
            xAxis={[
              {
                scaleType: "point",
                data: xLabels,
                height: "auto",
                tickLabelStyle: {
                  fontSize: 11,
                  ...(useRotatedLabels
                    ? { angle: -35, textAnchor: "end" }
                    : { textAnchor: "middle" }),
                },
              },
            ]}
            yAxis={[
              {
                width: 56,
                // MUI inverts textAnchor in RTL, which pushes Y labels into the plot.
                tickLabelStyle: { textAnchor: "end", fontSize: 11 },
              },
            ]}
            series={[
              {
                data: counts,
                label: t("reports.charts.ticketCount"),
                color: "#1976d2",
                showMark: true,
                curve: "natural",
              },
            ]}
            height={layoutHeight}
            margin={{
              top: showLabels ? 28 : 20,
              bottom: 16,
              left: 8,
              right: 16,
            }}
            grid={{ horizontal: true }}
            slots={{
              mark: (props) => {
                const { x, y, color, dataIndex, hidden } = props;
                if (hidden) {
                  return null;
                }
                return (
                  <g>
                    <circle cx={x} cy={y} r={3.5} fill={color} />
                    {showLabels && counts[dataIndex] != null && (
                      <text
                        x={x}
                        y={Number(y) - 10}
                        textAnchor="middle"
                        fontSize={11}
                        fill={color}
                      >
                        {counts[dataIndex]}
                      </text>
                    )}
                  </g>
                );
              },
            }}
          />
        )
      }
    </ChartCard>
  );
};

export default MonthlyLineChart;
